import { useDeferredValue } from "react"
import {
  Group,
  Loader,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
} from "@mantine/core"
import { useForm } from "@mantine/form"
import { useQueryClient } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import { CancelButton, SaveButton } from "@/-components/Buttons"
import { DefaultModal } from "@/-components/default-modal"
import { FormButtonGroup } from "@/-components/form-button-group"
import {
  getGetUsersWithoutExternalLoginQueryKey,
  SsoLinkUserTypeFilter,
  UserType,
  useCreateUserExternalLogins,
  useGetProviderConfigs,
  useGetUsersWithoutExternalLogin,
} from "@/orval/gen"
import {
  showErrorNotification,
  showSuccessNotification,
} from "@/utils/form-submission-notifications"
import { getSsoExternalIdClaim } from "./sso-external-id-claim"
import { PersonNameWithUserNameFallback } from "@/routes/-components/chat/ChatListForCurrentUser/PersonNameWithUserNameFallback"

interface Props {
  opened: boolean
  onClose: () => void
}

interface FormValues {
  providerConfigId: string
  userType: "" | SsoLinkUserTypeFilter
  searchString: string
  externalIds: Record<number, string>
}

export function BatchLinkExternalLoginsModal({ opened, onClose }: Props) {
  const { t } = useTranslation("app_server")
  const queryClient = useQueryClient()
  const form = useForm<FormValues>({
    initialValues: {
      providerConfigId: "",
      userType: "",
      searchString: "",
      externalIds: {},
    },
  })
  const providerConfigId = Number(form.values.providerConfigId) || undefined
  const searchString = useDeferredValue(form.values.searchString.trim())
  const { data: providers = [] } = useGetProviderConfigs()
  const selectedProvider = providers.find(
    (provider) => provider.id === providerConfigId
  )
  const externalIdClaim = getSsoExternalIdClaim(selectedProvider?.providerType)
  const externalIdLabel = externalIdClaim
    ? t("SsoExternalIdWithClaim", { claim: externalIdClaim })
    : t("SsoExternalId")
  const { data: users = [], isLoading } = useGetUsersWithoutExternalLogin(
    {
      providerConfigId,
      userType: form.values.userType || undefined,
      searchString: searchString || undefined,
      filterInactive: true,
    },
    { query: { enabled: providerConfigId !== undefined } }
  )
  const providedEntries = Object.entries(form.values.externalIds).filter(
    ([, externalId]) => externalId.trim().length > 0
  )
  const canSubmit = providerConfigId !== undefined && providedEntries.length > 0
  const { mutate: createLinks, isPending } = useCreateUserExternalLogins({
    mutation: {
      onError: showErrorNotification,
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: getGetUsersWithoutExternalLoginQueryKey(),
        })
        showSuccessNotification(t("Saved"))
        onClose()
      },
    },
  })

  const handleSubmit = () => {
    if (!providerConfigId || !canSubmit) return
    createLinks({
      data: providedEntries.map(([userId, externalId]) => ({
        userId: Number(userId),
        providerConfigId,
        externalId: externalId.trim(),
      })),
    })
  }

  return (
    <DefaultModal
      opened={opened}
      onClose={onClose}
      title={t("SsoLinkUsers")}
      size="xl"
    >
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack>
          <Group align="flex-start" grow>
            <Select
              label={t("SsoIdentityProviders")}
              required
              data={providers
                .filter((provider) => provider.id !== undefined)
                .map((provider) => ({
                  value: provider.id!.toString(),
                  label: provider.name ?? "",
                }))}
              {...form.getInputProps("providerConfigId")}
              onChange={(value) => {
                form.setFieldValue("providerConfigId", value ?? "")
                form.setFieldValue("externalIds", {})
              }}
            />
            <Select
              label={t("UserType")}
              clearable
              data={[
                {
                  value: SsoLinkUserTypeFilter.User,
                  label: t("User"),
                },
                {
                  value: SsoLinkUserTypeFilter.WebUser,
                  label: t("SsoWebUser"),
                },
                {
                  value: SsoLinkUserTypeFilter.WindowsUsers,
                  label: t("SsoWindowsUsers"),
                },
              ]}
              {...form.getInputProps("userType")}
              onChange={(value) => {
                form.setFieldValue(
                  "userType",
                  (value as SsoLinkUserTypeFilter) ?? ""
                )
                form.setFieldValue("externalIds", {})
              }}
            />
            <TextInput
              label={t("Search")}
              {...form.getInputProps("searchString")}
            />
          </Group>

          {!providerConfigId ? null : isLoading ? (
            <Group justify="center" py="xl">
              <Loader />
            </Group>
          ) : (
            <Table.ScrollContainer minWidth={640} maxHeight="50vh">
              <Table striped highlightOnHover stickyHeader>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>{t("Username")}</Table.Th>
                    <Table.Th>{t("UserType")}</Table.Th>
                    <Table.Th>{externalIdLabel}</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {users.map((user) => (
                    <Table.Tr key={user.userId}>
                      <Table.Td>
                        <PersonNameWithUserNameFallback userId={user.userId} />
                        {user.personId && (
                          <Text c="dimmed" size="xs">
                            {user.userName}
                          </Text>
                        )}
                      </Table.Td>
                      <Table.Td>
                        {user.userType === UserType.User
                          ? t("User")
                          : t("SsoWebUser")}
                      </Table.Td>
                      <Table.Td>
                        <TextInput
                          aria-label={externalIdLabel}
                          value={form.values.externalIds[user.userId] ?? ""}
                          onChange={(event) =>
                            form.setFieldValue("externalIds", {
                              ...form.values.externalIds,
                              [user.userId]: event.currentTarget.value,
                            })
                          }
                        />
                      </Table.Td>
                    </Table.Tr>
                  ))}
                  {users.length === 0 && (
                    <Table.Tr>
                      <Table.Td colSpan={3}>
                        <Text c="dimmed" ta="center">
                          {t("NothingFound")}
                        </Text>
                      </Table.Td>
                    </Table.Tr>
                  )}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          )}

          <FormButtonGroup>
            <CancelButton onClick={onClose} />
            <SaveButton
              children={t("Link")}
              disabled={!canSubmit}
              loading={isPending}
            />
          </FormButtonGroup>
        </Stack>
      </form>
    </DefaultModal>
  )
}
